import { useRef, useEffect, useState } from "react";
import {
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandList,
} from "@/components/ui/command";
import { Button } from "@/components/ui/button";
import { ChevronsUpDown } from "lucide-react";
import { UseFormReturn } from "react-hook-form";
import { OrderCreateFormValues } from "@/types/order-type";
import { Customer } from "@/types/customer-type";
import { useCustomerSearch } from "./hooks/use-customer-search";
import { CustomerListItem } from "./customer-list-item";

interface CustomerSelectionDropdownProps {
  form: UseFormReturn<OrderCreateFormValues>;
  selectedCustomer: Customer | null;
  watchCustomerId: string;
  allCustomers: Customer[];
}

export function CustomerSelectionDropdown({
  form,
  selectedCustomer,
  watchCustomerId,
  allCustomers,
}: CustomerSelectionDropdownProps) {
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [popoverWidth, setPopoverWidth] = useState<number | undefined>(
    undefined
  );

  const {
    allCustomers: searchCustomers,
    isLoading,
    isLast,
    isLoadingMore,
    handleLoadMore,
    reset,
  } = useCustomerSearch({
    searchTerm,
    isOpen: open,
  });

  // Update popover width when trigger is mounted or opened
  useEffect(() => {
    if (triggerRef.current) {
      setPopoverWidth(triggerRef.current.offsetWidth);
    }
  }, [open]);

  // Reset search and pagination when popover closes
  useEffect(() => {
    if (!open) {
      setSearchTerm("");
      reset();
    }
  }, [open, reset]);

  const getDisplayName = () => {
    if (selectedCustomer) {
      return selectedCustomer.name;
    }
    if (watchCustomerId) {
      const customer = allCustomers.find(
        (c) => String(c.id) === String(watchCustomerId)
      );
      return customer?.name || "";
    }
    return "";
  };

  const displayCustomers = open ? searchCustomers : allCustomers;

  return (
    <FormField
      control={form.control}
      name="customerId"
      render={({ field }) => (
        <FormItem>
          <FormControl>
            <Popover open={open} onOpenChange={setOpen}>
              <PopoverTrigger asChild>
                <Button
                  ref={triggerRef}
                  variant="outline"
                  role="combobox"
                  aria-expanded={open}
                  className="w-full justify-between"
                  disabled={isLoading && !open}
                >
                  {getDisplayName() || "Select customer..."}
                  <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent
                className="p-0"
                align="start"
                style={
                  popoverWidth ? { width: `${popoverWidth}px` } : undefined
                }
              >
                <Command shouldFilter={false}>
                  <CommandInput
                    placeholder="Search by name, phone, email..."
                    value={searchTerm}
                    onValueChange={setSearchTerm}
                  />
                  <CommandList>
                    <CommandEmpty>
                      {isLoading ? "Searching..." : "No customers found."}
                    </CommandEmpty>
                    <CommandGroup>
                      {displayCustomers.map((customer) => {
                        const isSelected =
                          String(customer.id) === String(field.value);
                        return (
                          <CustomerListItem
                            key={String(customer.id)}
                            customer={customer}
                            isSelected={isSelected}
                            onSelect={() => {
                              field.onChange(String(customer.id));
                              setOpen(false);
                              setSearchTerm("");
                            }}
                          />
                        );
                      })}
                    </CommandGroup>
                    {!isLast && displayCustomers.length > 0 && (
                      <div className="p-2 border-t">
                        <Button
                          variant="ghost"
                          className="w-full"
                          onClick={handleLoadMore}
                          disabled={isLoadingMore || isLoading}
                        >
                          {isLoadingMore || isLoading
                            ? "Loading..."
                            : "Load More"}
                        </Button>
                      </div>
                    )}
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );
}
